import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { ArrowDownRight, ArrowUpRight, Boxes, BarChart3, Users, FileSpreadsheet, Wallet, Receipt, AlertTriangle, Wrench } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";

const ICONS = {
  cash: Wallet,
  receivables: Receipt,
  payables: Receipt,
  overdue: AlertTriangle,
  assets_ok: Boxes,
  assets_service: Wrench,
  jobs: BarChart3,
  hr_docs: Users,
  spreadsheets: FileSpreadsheet,
};

const SOURCE_COLOR = {
  Xero: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Aroflo: "text-gbe bg-blue-50 border-blue-200",
  SharePoint: "text-violet-800 bg-violet-50 border-violet-200",
  Operations: "text-gbe bg-blue-50 border-blue-200",
  HSEQ: "text-amber-700 bg-amber-50 border-amber-200",
  Fleet: "text-emerald-700 bg-emerald-50 border-emerald-200",
  HR: "text-violet-800 bg-violet-50 border-violet-200",
  Assets: "text-gbe bg-blue-50 border-blue-200",
  "Capital Planning": "text-gbe bg-blue-50 border-blue-200",
  Procurement: "text-amber-700 bg-amber-50 border-amber-200",
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => { setData(r.data); setLoading(false); });
  }, []);

  return (
    <AppLayout
      title={data?.role_label || "Dashboard"}
      subtitle={data?.scope_label || "Live view across your data"}
    >
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-28 bg-white border border-slate-200 rounded-lg shimmer" />
          ))}
        </div>
      ) : (
        <>
          {/* KPI Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.cards.map((c) => {
              const Icon = ICONS[c.key] || BarChart3;
              const value = c.format === "currency" ? fmtCurrency(c.value) : fmtNumber(c.value);
              const positive = (c.delta ?? 0) >= 0;
              const bad = c.key === "overdue" || c.key === "assets_service" || c.key === "payables";
              return (
                <div
                  key={c.key}
                  data-testid={TID.kpiCard(c.key)}
                  className="kpi-accent bg-white border border-slate-200 rounded-lg p-5 pl-6 card-hover"
                >
                  <div className="flex items-start justify-between">
                    <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{c.label}</div>
                    <Icon className="w-4 h-4 text-slate-400" />
                  </div>
                  <div
                    data-testid={TID.kpiValue(c.key)}
                    className="mt-3 text-2xl font-display font-semibold text-slate-900 tabular-nums"
                  >
                    {value}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${SOURCE_COLOR[c.source] || "text-slate-600 bg-slate-50 border-slate-200"}`}>
                      {c.source}
                    </span>
                    {typeof c.delta === "number" && c.delta !== 0 && (
                      <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${(!bad && positive) || (bad && !positive) ? "text-emerald-700" : "text-rose-600"}`}>
                        {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {Math.abs(c.delta)}%
                      </span>
                    )}
                    {c.hint && <span className="text-[11px] text-slate-400">{c.hint}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Chart + Activity */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {data.revenue_series && data.revenue_series.length > 0 ? (
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">Revenue vs Expenses</h3>
                  <p className="text-xs text-slate-500">Last 6 months · from Xero</p>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gbe" />Revenue</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300" />Expenses</span>
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.revenue_series} margin={{ top: 8, right: 0, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Math.round(v/1000)}k`} />
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
                    <Bar dataKey="revenue" fill="#00334C" radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="expenses" fill="#cbd5e1" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            ) : null}

            <div className={`${data.revenue_series && data.revenue_series.length > 0 ? "" : "lg:col-span-3"} bg-white border border-slate-200 rounded-lg p-5`}>
              <h3 className="font-display text-lg font-semibold text-slate-900">Recent activity</h3>
              <p className="text-xs text-slate-500 mb-4">Across all connected systems</p>
              <ul className="space-y-3">
                {data.recent_activity.map((a, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border ${SOURCE_COLOR[a.source]}`}>
                      {a.source}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800">{a.text}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{a.when}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
